import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDate, ordinal } from "../lib/format";
import { highestAttempt } from "../lib/jobStatus";
import { ReviewNoteBox } from "../components/Common";
import { TOGGLE_CALLBACK_EVENT } from "./fields";
import { POST_MOVE_ATTEMPTS, PostMoveCompleteButton } from "./TabPostMove";

/**
 * "1st Attempt — Done" with a dropdown for the 2nd and 3rd attempts. Used on
 * the Introduction call, the Confirmation call and the Post Move screen;
 * `onLog(n)` records attempt n. Once an attempt is logged the main button
 * reads "2nd Attempt — Done" etc., so the MC sees the pick took effect
 * (Asana 1219375712610643); `attempts` is that screen's list ({n}[]).
 * `max`: the Post Move call has a 4th attempt (Asana 1220092199815254).
 */
const AttemptButton = ({ onLog, dropUp, attempts, max = 3 }) => {
  const [open, setOpen] = useState(false);
  const logged = highestAttempt(attempts);
  const mainLabel = `${ordinal(logged || 1)} Attempt — Done`;
  const menuItem = (n, className, label) => (
    <button
      type="button"
      className={`btn small ${className}`}
      style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderRadius: 0 }}
      onClick={() => {
        onLog(n);
        setOpen(false);
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      <button
        className={`btn ${logged ? "success" : "warning"}`}
        title={logged ? `${ordinal(logged)} attempt logged` : "Log the 1st attempt"}
        style={{
          padding: "7px 9px",
          fontSize: "11.5px",
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
        onClick={() => onLog(logged ? Math.min(logged + 1, max) : 1)}
      >
        <Icon name="check" />
        {mainLabel}
      </button>
      <button
        className="btn warning"
        aria-label="More attempts"
        aria-expanded={open}
        style={{
          padding: "7px 8px",
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: "1px solid rgba(255,255,255,.35)",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="chevronDown" />
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            [dropUp ? "bottom" : "top"]: "115%",
            left: 0,
            background: "#fff",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-md)",
            minWidth: 170,
            zIndex: 30,
            overflow: "hidden",
          }}
        >
          {menuItem(1, "warning", "1st attempt — Done")}
          {menuItem(2, "orange", "2nd attempt — Done")}
          {menuItem(3, "solid-red", "3rd attempt — Done")}
          {max >= 4 ? menuItem(4, "solid-red", "4th attempt — Done") : null}
        </div>
      ) : null}
    </div>
  );
};

const DoneBadge = ({ children }) => (
  <span className="badge resolved" style={{ padding: "8px 12px" }}>
    <span className="bdot" />
    {children}
  </span>
);

/**
 * The job's action bar: the call, plus the actions that move the job on from
 * the section being viewed. Shared by the popup footer and the job page
 * header, so both behave the same way.
 */
export const JobActions = ({
  job,
  isLD,
  activeTab,
  actions,
  dropUp = true,
}) => {
  // No Send text / Send email on any screen (Asana 1220000351335830).
  const showConfirmJob =
    activeTab === "s3" &&
    !job.confirmCallDone &&
    job.status !== "in-progress" &&
    job.status !== "move-completed" &&
    !job.status.startsWith("post-move");

  return (
    <>
      <button className="btn primary" onClick={() => actions.callClient(job.id)}>
        <Icon name="phone" />
        Call client
      </button>

      {/* Post Move Completed, lined up with Call client and the attempts (Asana 1220099429071097, 7.3). */}
      {activeTab === "s5" && job.status.startsWith("post-move") && !job.postMoveClosed ? (
        <PostMoveCompleteButton job={job} actions={actions} />
      ) : null}

      {job.status === "confirmed-update" ? (
        <ReviewNoteBox
          align={dropUp ? "up" : "down"}
          onReviewed={(text) => actions.markRevisionReviewed(job.id, text)}
        />
      ) : null}

      {activeTab === "s3" && !job.confirmCallDone ? (
        <AttemptButton
          onLog={(n) => actions.logConfirmAttemptN(job.id, n)}
          dropUp={dropUp}
          attempts={job.confirmAttempts}
        />
      ) : null}

      {showConfirmJob ? (
        <button className="btn success" onClick={() => actions.confirmJob(job.id)}>
          <Icon name="check" />
          Confirm Job
        </button>
      ) : null}

      {activeTab === "s3" && job.confirmCallDone ? <DoneBadge>Job Confirmed</DoneBadge> : null}

      {activeTab === "s2" ? (
        <>
          {!job.introCallDone ? (
            <AttemptButton
              onLog={(n) => actions.logAttemptN(job.id, n)}
              dropUp={dropUp}
              attempts={job.introAttempts}
            />
          ) : null}
          <button
            type="button"
            className={`btn ${job.callbackDate ? "success" : ""}`}
            onClick={() => window.dispatchEvent(new Event(TOGGLE_CALLBACK_EVENT))}
          >
            <Icon name="phone" />
            {job.callbackDate
              ? "Callback: " + fmtDate(job.callbackDate) + (job.callbackTime ? " " + job.callbackTime : "")
              : "Schedule callback"}
          </button>
          {job.introCallDone ? (
            <DoneBadge>Intro Call Completed</DoneBadge>
          ) : (
            <button className="btn success" onClick={() => actions.markIntro(job.id)}>
              <Icon name="check" />
              Intro Call Completed
            </button>
          )}
        </>
      ) : null}

      {/* Post Move: 1st to 4th post-move call attempts + callback, until the call is made (Asana 1219375712610643). */}
      {activeTab === "s5" && job.status.startsWith("post-move") && !job.postMoveCallDone && !job.postMoveClosed ? (
        <>
          <AttemptButton
            onLog={(n) => actions.logPostMoveAttemptN(job.id, n)}
            dropUp={dropUp}
            attempts={job.postMoveAttempts}
            max={POST_MOVE_ATTEMPTS}
          />
          <button
            type="button"
            className={`btn ${job.callbackDate ? "success" : ""}`}
            onClick={() => window.dispatchEvent(new Event(TOGGLE_CALLBACK_EVENT))}
          >
            <Icon name="phone" />
            {job.callbackDate
              ? "Callback: " + fmtDate(job.callbackDate) + (job.callbackTime ? " " + job.callbackTime : "")
              : "Schedule callback"}
          </button>
        </>
      ) : null}

      {activeTab === "s4t" && isLD ? <SitFooter job={job} actions={actions} /> : null}
    </>
  );
};

/**
 * In Transit footer (design of 2026-10-24): "SIT Local Confirmed" checks the delivery
 * dates first; "Move to Delivery Day in Progress" needs the crew confirmed. Each says
 * why when it cannot go ahead.
 */
const SitFooter = ({ job, actions }) => {
  const [error, setError] = useState("");
  const sitDone =
    job.status === "ld-sit-confirmed" ||
    job.status === "ld-delivery-local" ||
    job.status === "ld-driver-delivery" ||
    !!job.etaConfirmed ||
    !!job.sitLocalConfirmed;
  const inDeliveryDay = job.status === "ld-delivery-local";
  return (
    <>
      {sitDone ? (
        <button type="button" className="btn success" disabled>
          <Icon name="check" />
          SIT Local Confirmed
        </button>
      ) : (
        <button
          type="button"
          className="btn success"
          onClick={() => setError(actions.markEtaConfirmed(job.id) || "")}
        >
          <Icon name="check" />
          SIT Local Confirmed
        </button>
      )}
      {inDeliveryDay ? (
        <button type="button" className="btn success" disabled>
          <Icon name="truck" />
          In Delivery Day in Progress
        </button>
      ) : job.status === "ld-sit-confirmed" ? (
        <button
          type="button"
          className="btn primary"
          onClick={() => setError(actions.moveToDeliveryDay(job.id) || "")}
        >
          <Icon name="truck" />
          Move to Delivery Day in Progress
        </button>
      ) : (
        <button type="button" className="btn" disabled title="Confirm SIT Local first">
          <Icon name="truck" />
          Move to Delivery Day in Progress
        </button>
      )}
      {error ? (
        <span className="foot-error" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
};
